import { useEffect, useState } from 'react';
import { Alert } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  githubActionsApiRef,
  EntityRecentGithubActionsRunsCard,
} from '@backstage-community/plugin-github-actions';

const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

/**
 * Wraps the recent-runs card. The stock card catches its own 404 and renders a
 * raw "HttpError: Not Found" panel with no repo context. We probe the repo
 * first (getDefaultBranch = GET /repos/{owner}/{repo}); on failure we show a
 * message that names the repo and never mount the failing card.
 */
export const GithubActionsRunsCard = (props: { limit?: number }) => {
  const { entity } = useEntity();
  const api = useApi(githubActionsApiRef);

  const slug = entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION];
  const [owner, repo] = slug ? slug.split('/') : [];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    const probe = async () => {
      if (!owner || !repo) {
        throw new Error(
          `missing or malformed "${PROJECT_SLUG_ANNOTATION}" annotation (got "${slug ?? 'none'}")`,
        );
      }
      await api.getDefaultBranch({ owner, repo });
    };

    probe()
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [owner, repo, slug, api]);

  if (loading) {
    return (
      <InfoCard title="Recent GitHub Actions runs">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    const isNotFound = /not found/i.test(error.message);
    const repoLabel = slug ? `"${slug}"` : 'this component';
    return (
      <InfoCard title="Recent GitHub Actions runs">
        <Alert severity="warning">
          {isNotFound
            ? `Repository ${repoLabel} was not found. Check the "${PROJECT_SLUG_ANNOTATION}" annotation and that the GitHub token has access.`
            : `GitHub Actions unavailable for ${repoLabel}: ${error.message}`}
        </Alert>
      </InfoCard>
    );
  }

  return <EntityRecentGithubActionsRunsCard limit={props.limit} />;
};
